import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const INCLUDE_COLOR = new THREE.Color('#22c55e');
const EXCLUDE_COLOR = new THREE.Color('#f43f5e');
const PENDING_COLOR = new THREE.Color('#facc15');
const FEEDBACK_DURATION_MS = 450;

export function useStepVisibilityFeedback() {
  const timeoutIdsRef = useRef<number[]>([]);
  const activeHighlightRef = useRef<{
    restoreEntries: Array<{
      mesh: THREE.Mesh;
      originalMaterial: THREE.Material | THREE.Material[];
      temporaryMaterials: THREE.Material[];
    }>;
  } | null>(null);

  const clearActiveHighlight = () => {
    if (!activeHighlightRef.current) return;

    activeHighlightRef.current.restoreEntries.forEach(({ mesh, originalMaterial, temporaryMaterials }) => {
      mesh.material = originalMaterial;
      temporaryMaterials.forEach((material) => material.dispose());
    });

    activeHighlightRef.current = null;
  };

  useEffect(() => {
    return () => {
      timeoutIdsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutIdsRef.current = [];
      clearActiveHighlight();
    };
  }, []);

  const applyHighlight = (target: THREE.Object3D | null, nextColor: THREE.Color) => {
    if (!target) return;

    clearActiveHighlight();

    const restoreEntries: Array<{
      mesh: THREE.Mesh;
      originalMaterial: THREE.Material | THREE.Material[];
      temporaryMaterials: THREE.Material[];
    }> = [];

    target.traverse((object) => {
      if (!(object as THREE.Mesh).isMesh) return;
      const mesh = object as THREE.Mesh;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const temporaryMaterials = materials.map((material) => {
        const clone = material.clone();

        if ('color' in clone && clone.color instanceof THREE.Color) {
          clone.color = nextColor.clone();
        }

        if ('emissive' in clone && clone.emissive instanceof THREE.Color) {
          clone.emissive = nextColor.clone();
          if ('emissiveIntensity' in clone) {
            (clone as THREE.MeshStandardMaterial).emissiveIntensity = 0.7;
          }
        }

        return clone;
      });

      restoreEntries.push({
        mesh,
        originalMaterial: mesh.material,
        temporaryMaterials,
      });

      mesh.material = Array.isArray(mesh.material) ? temporaryMaterials : temporaryMaterials[0];
    });

    activeHighlightRef.current = { restoreEntries };
  };

  const preview = (target: THREE.Object3D | null) => {
    applyHighlight(target, PENDING_COLOR);
  };

  const clear = () => {
    clearActiveHighlight();
  };

  const flash = (target: THREE.Object3D | null, included: boolean) => {
    applyHighlight(target, included ? INCLUDE_COLOR : EXCLUDE_COLOR);

    const timeoutId = window.setTimeout(() => {
      clearActiveHighlight();
      timeoutIdsRef.current = timeoutIdsRef.current.filter((activeId) => activeId !== timeoutId);
    }, FEEDBACK_DURATION_MS);

    timeoutIdsRef.current.push(timeoutId);
  };

  return { preview, clear, flash };
}
